import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/auth-context';
import { authFetch } from '@/utils/auth-fetch';
import { API_URL } from '@/config/api';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'react-hot-toast';
import {
  Mail,
  Clock,
  Calendar,
  BarChart,
  Users,
  Brain,
  Lightbulb,
  Send,
  Pause,
  Play,
  Loader2,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

interface Site {
  id: string;
  name: string;
  url: string;
}

interface Subscription {
  id?: string;
  site_id: string;
  frequency: 'daily' | 'weekly' | 'monthly';
  time_preference: string;
  timezone: string;
  include_analytics: boolean;
  include_behavior: boolean;
  include_segments: boolean;
  include_ai_insights: boolean;
  include_recommendations: boolean;
  is_active: boolean;
  is_paused: boolean;
  last_sent_at?: string;
  next_scheduled_at?: string;
  total_sent?: number;
  site_name?: string;
}

const TIMEZONES = [
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'Europe/London', label: 'London (GMT)' },
  { value: 'Europe/Paris', label: 'Paris (CET)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEDT)' },
];

const DELIVERY_TIMES = [
  { value: '06:00', label: '6:00 AM' },
  { value: '07:00', label: '7:00 AM' },
  { value: '08:00', label: '8:00 AM' },
  { value: '09:00', label: '9:00 AM' },
  { value: '10:00', label: '10:00 AM' },
  { value: '12:00', label: '12:00 PM' },
  { value: '14:00', label: '2:00 PM' },
  { value: '16:00', label: '4:00 PM' },
  { value: '18:00', label: '6:00 PM' },
];

export function InsightsSubscriptions() {
  const { user } = useAuth();
  const [sites, setSites] = useState<Site[]>([]);
  const [subscriptions, setSubscriptions] = useState<Map<string, Subscription>>(new Map());
  const [selectedSiteId, setSelectedSiteId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  useEffect(() => {
    fetchSitesAndSubscriptions();
  }, []);

  const fetchSitesAndSubscriptions = async () => {
    try {
      // Fetch user's sites
      console.log('[InsightsSubscriptions] Fetching sites from:', `${API_URL}/users/me/sites`);
      const sitesResponse = await authFetch(`${API_URL}/users/me/sites`);
      console.log('[InsightsSubscriptions] Sites response status:', sitesResponse.status);
      if (!sitesResponse.ok) {
        throw new Error(`Sites API error: ${sitesResponse.status}`);
      }
      const sitesData = await sitesResponse.json();
      console.log('[InsightsSubscriptions] Sites data:', sitesData);
      const sites = sitesData.sites || [];
      setSites(Array.isArray(sites) ? sites : []);

      // Fetch existing subscriptions
      const subsResponse = await authFetch(`${API_URL}/insights/subscriptions`);
      if (!subsResponse.ok) {
        throw new Error(`Subscriptions API error: ${subsResponse.status}`);
      }
      const subsData = await subsResponse.json();
      
      const subsMap = new Map<string, Subscription>();
      if (subsData.subscriptions && Array.isArray(subsData.subscriptions)) {
        subsData.subscriptions.forEach((sub: Subscription) => {
          subsMap.set(sub.site_id, sub);
        });
      }
      setSubscriptions(subsMap);

      // Select first site if available
      if (sites.length > 0 && !selectedSiteId) {
        setSelectedSiteId(sites[0].id);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load subscription settings');
    } finally {
      setLoading(false);
    }
  };

  const getCurrentSubscription = (): Subscription => {
    return subscriptions.get(selectedSiteId) || {
      site_id: selectedSiteId,
      frequency: 'weekly',
      time_preference: '09:00',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
      include_analytics: true,
      include_behavior: true,
      include_segments: true,
      include_ai_insights: true,
      include_recommendations: true,
      is_active: false,
      is_paused: false
    };
  };

  const updateSubscription = (updates: Partial<Subscription>) => {
    const current = getCurrentSubscription();
    const updated = { ...current, ...updates };
    
    const newMap = new Map(subscriptions);
    newMap.set(selectedSiteId, updated);
    setSubscriptions(newMap);
  };

  const saveSubscription = async () => {
    setSaving(true);
    try {
      const subscription = getCurrentSubscription();
      
      const response = await authFetch(`${API_URL}/insights/subscriptions/${selectedSiteId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(subscription)
      });

      if (!response.ok) throw new Error('Failed to save subscription');
      
      const data = await response.json();
      updateSubscription(data.subscription);
      toast.success(data.message || 'Subscription saved successfully');
    } catch (error) {
      console.error('Error saving subscription:', error);
      toast.error('Failed to save subscription settings');
    } finally {
      setSaving(false);
    }
  };

  const sendTestEmail = async () => {
    setSendingTest(true);
    try {
      const response = await authFetch(`${API_URL}/insights/subscriptions/${selectedSiteId}/test`, {
        method: 'POST'
      });

      if (!response.ok) throw new Error('Failed to send test email');
      
      const data = await response.json();
      toast.success(`Test email sent to ${data.email}`);
    } catch (error) {
      console.error('Error sending test email:', error);
      toast.error('Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  const togglePause = async () => {
    const current = getCurrentSubscription();
    if (!current.id) {
      toast.error('Please save your subscription first');
      return;
    }

    try {
      const response = await authFetch(`${API_URL}/insights/subscriptions/${selectedSiteId}/pause`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_paused: !current.is_paused })
      });

      if (!response.ok) throw new Error('Failed to update subscription');
      
      const data = await response.json();
      updateSubscription(data.subscription);
      toast.success(data.message);
    } catch (error) {
      console.error('Error toggling pause:', error);
      toast.error('Failed to update subscription');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  const subscription = getCurrentSubscription();
  const selectedSite = sites.find(s => s.id === selectedSiteId);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">Email Insights Subscriptions</h2>
        <p className="text-gray-600">
          Receive regular analytics insights and recommendations directly in your inbox
        </p>
      </div>

      {sites.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No sites found. Add a site first to set up email subscriptions.</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Tabs value={selectedSiteId} onValueChange={setSelectedSiteId}>
          <TabsList className="grid w-full" style={{ gridTemplateColumns: `repeat(${Math.min(sites.length, 4)}, 1fr)` }}>
            {sites.map(site => (
              <TabsTrigger key={site.id} value={site.id}>
                {site.name}
              </TabsTrigger>
            ))}
          </TabsList>

          {sites.map(site => (
            <TabsContent key={site.id} value={site.id} className="space-y-6">
              {/* Subscription Status */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Subscription Status</CardTitle>
                      <CardDescription>
                        Email insights for {site.name}
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-4">
                      {subscription.is_active && (
                        subscription.is_paused ? (
                          <Badge variant="secondary">
                            <Pause className="w-3 h-3 mr-1" />
                            Paused
                          </Badge>
                        ) : (
                          <Badge variant="default" className="bg-green-600">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Active
                          </Badge>
                        )
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="active">Enable Email Insights</Label>
                      <p className="text-sm text-gray-500">
                        Receive scheduled analytics reports via email
                      </p>
                    </div>
                    <Switch
                      id="active"
                      checked={subscription.is_active}
                      onCheckedChange={(checked) => updateSubscription({ is_active: checked })}
                    />
                  </div>

                  {subscription.is_active && subscription.last_sent_at && (
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Last sent:</span>
                        <span>{new Date(subscription.last_sent_at).toLocaleString()}</span>
                      </div>
                      {subscription.next_scheduled_at && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Next scheduled:</span>
                          <span>{new Date(subscription.next_scheduled_at).toLocaleString()}</span>
                        </div>
                      )}
                      {subscription.total_sent !== undefined && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Total emails sent:</span>
                          <span>{subscription.total_sent}</span>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Delivery Settings */}
              <Card>
                <CardHeader>
                  <CardTitle>Delivery Settings</CardTitle>
                  <CardDescription>
                    Choose when and how often to receive insights
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="frequency">
                        <Calendar className="w-4 h-4 inline mr-1" />
                        Frequency
                      </Label>
                      <Select
                        value={subscription.frequency}
                        onValueChange={(value: any) => updateSubscription({ frequency: value })}
                      >
                        <SelectTrigger id="frequency">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="daily">Daily</SelectItem>
                          <SelectItem value="weekly">Weekly (Mondays)</SelectItem>
                          <SelectItem value="monthly">Monthly (1st day)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="time">
                        <Clock className="w-4 h-4 inline mr-1" />
                        Delivery Time
                      </Label>
                      <Select
                        value={subscription.time_preference}
                        onValueChange={(value) => updateSubscription({ time_preference: value })}
                      >
                        <SelectTrigger id="time">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {DELIVERY_TIMES.map(time => (
                            <SelectItem key={time.value} value={time.value}>
                              {time.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="timezone">Timezone</Label>
                      <Select
                        value={subscription.timezone}
                        onValueChange={(value) => updateSubscription({ timezone: value })}
                      >
                        <SelectTrigger id="timezone">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TIMEZONES.map(tz => (
                            <SelectItem key={tz.value} value={tz.value}>
                              {tz.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Content Preferences */}
              <Card>
                <CardHeader>
                  <CardTitle>Content Preferences</CardTitle>
                  <CardDescription>
                    Select which insights to include in your emails
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <BarChart className="w-5 h-5 text-purple-600" />
                        <div>
                          <Label htmlFor="analytics">Analytics Overview</Label>
                          <p className="text-sm text-gray-500">
                            Traffic, conversions, and key metrics
                          </p>
                        </div>
                      </div>
                      <Switch
                        id="analytics"
                        checked={subscription.include_analytics}
                        onCheckedChange={(checked) => updateSubscription({ include_analytics: checked })}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Users className="w-5 h-5 text-blue-600" />
                        <div>
                          <Label htmlFor="behavior">Visitor Behavior</Label>
                          <p className="text-sm text-gray-500">
                            Engagement patterns and maturity levels
                          </p>
                        </div>
                      </div>
                      <Switch
                        id="behavior"
                        checked={subscription.include_behavior}
                        onCheckedChange={(checked) => updateSubscription({ include_behavior: checked })}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Users className="w-5 h-5 text-green-600" />
                        <div>
                          <Label htmlFor="segments">Customer Segments</Label>
                          <p className="text-sm text-gray-500">
                            Segment distribution and insights
                          </p>
                        </div>
                      </div>
                      <Switch
                        id="segments"
                        checked={subscription.include_segments}
                        onCheckedChange={(checked) => updateSubscription({ include_segments: checked })}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Brain className="w-5 h-5 text-purple-600" />
                        <div>
                          <Label htmlFor="ai">AI Insights</Label>
                          <p className="text-sm text-gray-500">
                            AI-generated narratives and summaries
                          </p>
                        </div>
                      </div>
                      <Switch
                        id="ai"
                        checked={subscription.include_ai_insights}
                        onCheckedChange={(checked) => updateSubscription({ include_ai_insights: checked })}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Lightbulb className="w-5 h-5 text-yellow-600" />
                        <div>
                          <Label htmlFor="recommendations">Recommendations</Label>
                          <p className="text-sm text-gray-500">
                            Actionable growth opportunities
                          </p>
                        </div>
                      </div>
                      <Switch
                        id="recommendations"
                        checked={subscription.include_recommendations}
                        onCheckedChange={(checked) => updateSubscription({ include_recommendations: checked })}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Actions */}
              <div className="flex flex-wrap gap-4">
                <Button
                  onClick={saveSubscription}
                  disabled={saving}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Save Settings
                </Button>

                <Button
                  variant="outline"
                  onClick={sendTestEmail}
                  disabled={sendingTest || !subscription.is_active}
                >
                  {sendingTest ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Send Test Email
                </Button>

                {subscription.is_active && subscription.id && (
                  <Button
                    variant="outline"
                    onClick={togglePause}
                  >
                    {subscription.is_paused ? (
                      <>
                        <Play className="w-4 h-4 mr-2" />
                        Resume
                      </>
                    ) : (
                      <>
                        <Pause className="w-4 h-4 mr-2" />
                        Pause
                      </>
                    )}
                  </Button>
                )}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      )}
    </div>
  );
}